import { useEffect, useRef, useState } from "react";

import AdminPage from "./components/Admin/AdminPage";
import GuestHome from "./components/Home/GuestHome";
import Login from "./components/Login/Login";
import RSVPPage from "./components/RSVP/RSVPPage";
import StoryBook from "./components/Story/StoryBook";

import song from "./assets/song.mp4";

import "./App.css";

function App() {
  const isAdminPage =
    window.location.hash.toLowerCase() === "#/admin" ||
    window.location.pathname.toLowerCase() === "/admin";

  const [loggedIn, setLoggedIn] = useState(false);
  const [accessLevel, setAccessLevel] = useState("guest");

  // home | rsvp | story
  const [guestView, setGuestView] = useState("home");

  const [musicPlaying, setMusicPlaying] = useState(false);
  const [showMusicHint, setShowMusicHint] = useState(false);

  const audioRef = useRef(null);
  const shouldResumeMusicRef = useRef(false);

  const startMusic = async () => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    audio.volume = 0.38;
    audio.loop = true;

    try {
      await audio.play();
      setMusicPlaying(true);
      setShowMusicHint(false);
    } catch (error) {
      setShowMusicHint(true);
    }
  };

  const pauseMusic = () => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    audio.pause();
    setMusicPlaying(false);
  };

  const toggleMusic = async () => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    if (audio.paused) {
      await startMusic();
    } else {
      pauseMusic();
    }
  };

  const handleLoginSuccess = (level) => {
    setAccessLevel(level);
    setLoggedIn(true);
    setGuestView("home");
  };

  useEffect(() => {
    if (isAdminPage) {
      return undefined;
    }

    const pauseForBackground = () => {
      const audio = audioRef.current;

      if (!audio) {
        return;
      }

      shouldResumeMusicRef.current = !audio.paused;

      audio.pause();
      setMusicPlaying(false);
    };

    const resumeAfterBackground = async () => {
      const audio = audioRef.current;

      if (!audio || !shouldResumeMusicRef.current) {
        return;
      }

      try {
        await audio.play();
        setMusicPlaying(true);
        setShowMusicHint(false);
      } catch (error) {
        setShowMusicHint(true);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        pauseForBackground();
      } else {
        resumeAfterBackground();
      }
    };

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    window.addEventListener("pagehide", pauseForBackground);
    window.addEventListener("pageshow", resumeAfterBackground);

    return () => {
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );

      window.removeEventListener(
        "pagehide",
        pauseForBackground
      );

      window.removeEventListener(
        "pageshow",
        resumeAfterBackground
      );
    };
  }, [isAdminPage]);

  if (isAdminPage) {
    return <AdminPage />;
  }

  const renderGuestView = () => {
    if (guestView === "rsvp") {
      return (
        <RSVPPage
          onBack={() => setGuestView("home")}
          onOpenStory={() => setGuestView("story")}
        />
      );
    }

    if (guestView === "story") {
      return (
        <StoryBook
          fullAccess={accessLevel === "full"}
        />
      );
    }

    return (
      <GuestHome
        onOpenRsvp={() => setGuestView("rsvp")}
        onOpenStory={() => setGuestView("story")}
      />
    );
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={song}
        loop
        preload="auto"
      />

      {loggedIn ? (
        <>
          {guestView !== "home" && (
            <nav className="guestPortalNav">
              <button
                type="button"
                onClick={() => setGuestView("home")}
              >
                Home
              </button>

              <button
                type="button"
                className={
                  guestView === "rsvp" ? "active" : ""
                }
                onClick={() => setGuestView("rsvp")}
              >
                 Events & RSVP
              </button>

              <button
                type="button"
                className={
                  guestView === "story" ? "active" : ""
                }
                onClick={() => setGuestView("story")}
              >
                Our Story
              </button>
            </nav>
          )}

          {renderGuestView()}
        </>
      ) : (
        <Login
          onSuccess={handleLoginSuccess}
          onStartMusic={startMusic}
        />
      )}

      {loggedIn && (
        <>
          {showMusicHint && (
            <button
              type="button"
              className="musicHint"
              onClick={startMusic}
            >
              Tap to start music ♫
            </button>
          )}

          <button
            type="button"
            className={`musicButton ${
              musicPlaying ? "playing" : ""
            }`}
            onClick={toggleMusic}
            aria-label={
              musicPlaying ? "Pause music" : "Play music"
            }
          >
            {musicPlaying ? "♫" : "♡"}
          </button>
        </>
      )}
    </>
  );
}

export default App;